import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Printer } from "lucide-react";
import { apiClient } from "@/lib/api";
import { BRAND } from "@/lib/brand";

const money = (n) => (n === undefined || n === null ? "—" : `£${Number(n).toLocaleString("en-GB")}`);

export default function JobSheet() {
  const { ref } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get(`/admin/bookings/${ref}`);
        setBooking(data);
      } catch (e) {
        setError(e?.response?.status === 401 ? "auth" : "notfound");
      }
    })();
  }, [ref]);

  if (error === "auth") {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center p-6">
        <p>Please sign in at <a className="underline" href="/admin">/admin</a> first, then retry.</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center p-6">
        <p>Booking not found.</p>
      </div>
    );
  }
  if (!booking) {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center p-6">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const vehicle = [booking.vehicle_year, booking.vehicle_make, booking.vehicle_model]
    .filter(Boolean)
    .join(" ");

  return (
    <main data-testid="jobsheet-page" className="min-h-screen bg-white text-black print:bg-white">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 14mm; }
          body { background: #fff !important; }
        }
      `}</style>

      <div className="no-print bg-neutral-100 border-b border-neutral-300">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/admin" className="text-xs uppercase tracking-widest text-neutral-600 hover:text-black">
            ← Back to admin
          </a>
          <button
            data-testid="jobsheet-print"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 text-xs uppercase tracking-widest font-bold"
          >
            <Printer className="w-4 h-4" /> Print · Save as PDF
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-10 print:py-4">
        {/* Header */}
        <header className="flex items-start justify-between border-b-2 border-black pb-6 mb-8">
          <div className="flex items-center gap-4">
            <img src={BRAND.logoUrl} alt="MHJ" className="h-14 w-14 object-contain" />
            <div>
              <div className="font-extrabold tracking-tight text-lg leading-none">
                MHJ PRESTIGE TRANSPORT LTD
              </div>
              <div className="text-[11px] tracking-[0.2em] uppercase text-neutral-500 mt-1">
                Driver Job Sheet
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">Ref</div>
            <div className="font-mono font-extrabold text-2xl mt-1">{booking.booking_ref}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-2">
              Status · {booking.status?.replace(/_/g, " ")}
            </div>
          </div>
        </header>

        {/* Route */}
        <section className="mb-8">
          <SectionTitle>Route</SectionTitle>
          <div className="grid grid-cols-2 gap-6 mt-3">
            <Field label="Collection">{booking.pickup_location}</Field>
            <Field label="Delivery">{booking.dropoff_location}</Field>
            <Field label="Loaded miles">{booking.loaded_miles}</Field>
            <Field label="Requested">
              {booking.requested_collection_date} · {booking.requested_collection_time}
            </Field>
            {booking.flexibility && booking.flexibility !== "none" && (
              <Field label="Flexibility">{booking.flexibility}</Field>
            )}
            {booking.urgency_label !== "planned" && (
              <Field label="Urgency">{booking.urgency_label}</Field>
            )}
          </div>
        </section>

        {/* Vehicle */}
        <section className="mb-8">
          <SectionTitle>Vehicle</SectionTitle>
          <div className="grid grid-cols-2 gap-6 mt-3">
            <Field label="Registration">{booking.vehicle_registration || "—"}</Field>
            <Field label="Vehicle">{vehicle || "—"}</Field>
            <Field label="Type">{booking.vehicle_size}</Field>
            <Field label="Condition">{booking.condition}</Field>
            <Field label="Lowered">{booking.lowered}</Field>
            <Field label="Fuel · body">
              {[booking.vehicle_fuel, booking.vehicle_body_type].filter(Boolean).join(" · ") || "—"}
            </Field>
          </div>
        </section>

        {/* Customer */}
        <section className="mb-8">
          <SectionTitle>Customer</SectionTitle>
          <div className="grid grid-cols-2 gap-6 mt-3">
            <Field label="Name">{booking.customer_name}</Field>
            <Field label="Preferred contact">{booking.preferred_contact}</Field>
            <Field label="Phone">{booking.customer_phone}</Field>
            <Field label="Email">{booking.customer_email}</Field>
          </div>
        </section>

        {/* Access / auction */}
        <section className="mb-8">
          <SectionTitle>Access &amp; Collection</SectionTitle>
          <div className="grid grid-cols-2 gap-6 mt-3">
            <Field label="Access at collection">{booking.collection_access}</Field>
            <Field label="Access at delivery">{booking.delivery_access}</Field>
            {booking.is_auction && (
              <>
                <Field label="Auction facility">{booking.auction_facility || "—"}</Field>
                <Field label="Release / reference">{booking.auction_reference || "—"}</Field>
              </>
            )}
          </div>
          {booking.flags?.length ? (
            <div className="mt-4 border-l-2 border-amber-500 pl-3 text-sm">
              <div className="font-bold uppercase tracking-widest text-[10px] text-amber-700">Flags</div>
              <ul className="mt-1">
                {booking.flags.map((f, i) => (
                  <li key={i}>· {f.replace(/_/g, " ")}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>

        {/* Notes */}
        {booking.notes && (
          <section className="mb-8">
            <SectionTitle>Customer notes</SectionTitle>
            <p className="mt-3 whitespace-pre-wrap italic">“{booking.notes}”</p>
          </section>
        )}

        {/* Pricing summary */}
        <section className="mb-8 border-t-2 border-black pt-4">
          <SectionTitle>Pricing</SectionTitle>
          <table className="w-full mt-3 text-sm">
            <tbody>
              <tr>
                <td className="py-1 text-neutral-500 uppercase text-[10px] tracking-widest">Transport price</td>
                <td className="py-1 text-right font-bold">{money(booking.instant_booking_price)}</td>
              </tr>
              <tr>
                <td className="py-1 text-neutral-500 uppercase text-[10px] tracking-widest">Deposit paid</td>
                <td className="py-1 text-right">{money(booking.deposit_paid || booking.deposit_due)}</td>
              </tr>
              <tr className="border-t border-black">
                <td className="py-2 text-neutral-500 uppercase text-[10px] tracking-widest font-bold">Balance on completion</td>
                <td className="py-2 text-right font-extrabold text-lg">
                  {money(booking.balance_remaining)}
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Signatures */}
        <section className="mt-14 grid grid-cols-2 gap-8">
          <SignBox title="Collection — Customer sign" />
          <SignBox title="Delivery — Customer sign" />
        </section>

        <footer className="mt-14 pt-6 border-t border-neutral-300 text-[10px] uppercase tracking-widest text-neutral-500 flex justify-between">
          <span>MHJ Prestige Transport Ltd · {BRAND.location}</span>
          <span>{BRAND.phone} · {BRAND.email}</span>
        </footer>
      </div>
    </main>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="text-[10px] tracking-[0.3em] uppercase font-bold text-neutral-600">{children}</h2>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</div>
      <div className="text-base font-semibold mt-1 break-words">{children ?? "—"}</div>
    </div>
  );
}

function SignBox({ title }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-600 mb-8">{title}</div>
      <div className="border-b border-black h-14" />
      <div className="mt-2 grid grid-cols-2 gap-4 text-[10px] uppercase tracking-widest text-neutral-500">
        <span>Name</span>
        <span>Date · Odometer</span>
      </div>
    </div>
  );
}
